import { useEffect, useState, useRef } from 'react';
import {
  sampleDbzFromTileAsync, beamHeightKm, getBearing,
  bearingToCardinal, findNearestMetar, haversineKm, haversineMeters,
  formatProbeDistance,
} from '../utils/radarProbe';
import './ProbeOverlay.css';

const RECOMPUTE_INTERVAL = 120; // ms — ~8fps is plenty for a stationary probe
const HYSTERESIS_M       = 20_000;

export default function ProbeOverlay({ map, tileUrlTemplate, activeSite, metarData, distUnit = 'km', altUnit = 'km' }) {
  const [info, setInfo] = useState(null);

  const activeSiteRef    = useRef(activeSite);
  const metarDataRef     = useRef(metarData);
  const tileUrlRef       = useRef(tileUrlTemplate);
  const lastMetarRef     = useRef(null);
  const lastRecomputeRef = useRef(0);
  // Cache canvas rect — changes only on resize, not on every pan
  const rectRef          = useRef(null);

  useEffect(() => { activeSiteRef.current = activeSite;       }, [activeSite]);
  useEffect(() => { metarDataRef.current  = metarData;        }, [metarData]);
  useEffect(() => { tileUrlRef.current    = tileUrlTemplate;  }, [tileUrlTemplate]);

  useEffect(() => {
    if (!map) return;

    // Warm the rect cache immediately and refresh on resize only
    const refreshRect = () => { rectRef.current = map.getCanvas().getBoundingClientRect(); };
    refreshRect();
    window.addEventListener('resize', refreshRect);

    async function sample() {
      const now = performance.now();
      if (now - lastRecomputeRef.current < RECOMPUTE_INTERVAL) return;
      lastRecomputeRef.current = now;

      const rect  = rectRef.current;
      const cssCx = window.innerWidth  / 2 - rect.left;
      const cssCy = window.innerHeight / 2 - rect.top;

      const lngLat = map.unproject([cssCx, cssCy]);

      let distKm = null, heightKm = null, cardinal = null;
      const site = activeSiteRef.current;
      if (site) {
        distKm   = haversineKm(site.lon, site.lat, lngLat.lng, lngLat.lat);
        heightKm = beamHeightKm(distKm);
        cardinal = bearingToCardinal(getBearing(site.lon, site.lat, lngLat.lng, lngLat.lat));
      }

      const rawNearest = findNearestMetar(metarDataRef.current, lngLat.lng, lngLat.lat);
      let metarFeature = rawNearest;
      if (rawNearest && lastMetarRef.current) {
        const [lLng, lLat] = lastMetarRef.current.geometry.coordinates;
        const [nLng, nLat] = rawNearest.geometry.coordinates;
        const lastD = haversineMeters([lngLat.lng, lngLat.lat], [lLng, lLat]);
        const newD  = haversineMeters([lngLat.lng, lngLat.lat], [nLng, nLat]);
        if (newD >= lastD - HYSTERESIS_M) metarFeature = lastMetarRef.current;
      }
      lastMetarRef.current = metarFeature;

      let metarWind = null;
      if (metarFeature) {
        const p       = metarFeature.properties;
        const dirCard = typeof p.wdir === 'number' ? bearingToCardinal(p.wdir)
                      : p.wdir === 'VRB'           ? 'VRB'
                      : null;
        if (p.wspd != null || dirCard != null) {
          metarWind = { speed: p.wspd, dir: dirCard, station: p.id };
        }
      }

      // Sample directly from the currently-displayed radar tile PNG — not the
      // composited canvas — so road/shield colours never trigger false dBZ.
      const dbz = await sampleDbzFromTileAsync(tileUrlRef.current, lngLat.lng, lngLat.lat);
      setInfo({ dbz, distKm, heightKm, cardinal, metarWind });
    }

    sample(); // initial read
    map.on('move', sample);
    map.on('moveend', sample);
    return () => {
      map.off('move', sample);
      map.off('moveend', sample);
      window.removeEventListener('resize', refreshRect);
    };
  }, [map]);

  return (
    <>
      {/* Fixed crosshair — always at viewport centre */}
      <div className="sa-probe-center" aria-hidden="true">
        <ProbeCard info={info} distUnit={distUnit} altUnit={altUnit} />
        <svg className="sa-probe-reticle" width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="10" stroke="#38bdf8" strokeWidth="1.8" />
          <line x1="24" y1="4"  x2="24" y2="14" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="24" y1="34" x2="24" y2="44" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="4"  y1="24" x2="14" y2="24" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="34" y1="24" x2="44" y2="24" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="24" cy="24" r="2.2" fill="#38bdf8" fillOpacity="0.9" />
        </svg>
      </div>
    </>
  );
}

function ProbeCard({ info, distUnit, altUnit }) {
  const parts = [info?.dbz != null ? `${info.dbz} dBZ` : '— dBZ'];
  if (info?.distKm != null) {
    parts.push(formatProbeDistance(info.distKm, distUnit) + (info.cardinal ? ` ${info.cardinal}` : ''));
    if (info.heightKm != null) parts.push(`${formatProbeDistance(info.heightKm, altUnit)} AGL`);
  }

  let windLabel = null;
  if (info?.metarWind) {
    const { speed, dir, station } = info.metarWind;
    windLabel = `${dir ? dir + ' ' : ''}${speed != null ? speed + 'kt' : '—kt'} · ${station}`;
  }

  return (
    <div className="sa-probe-card">
      <span className="sa-probe-card__main">{parts.join(' · ')}</span>
      {windLabel && <span className="sa-probe-card__wind">{windLabel}</span>}
    </div>
  );
}
